import { Paper, Table, TableBody, TableContainer } from "@mui/material";
import { useRef, useState } from "react";
import { useAppSelector } from "../../../app/hooks";
import { Nullable } from "../../../common/types/nullable";
import { EditEntry, Entry, NewEntryTalentData } from "../entriesAPI";
import { selectAllEntries } from "../entriesSlice";
import { CopyEntries } from "../Entry/Copy/CopyEntries";
import { CollabsibleStates, GroupKey, GroupedData } from "../types";
import BottomActionBar from "./BottomActionBar";
import EntriesHeaders from "./EntriesHeaders";
import EntriesModals from "./EntriesModals";
import GroupEntryRow from "./GroupEntryRow";
import GroupRow from "./GroupRow";

type Props = {
  groupedData: GroupedData;
  groups: GroupKey[];
  supplementHeaders: string[];
  expensesHeaders: string[];
  selectedEntries: Entry[];
  setSelectedEntries: (val: Entry[]) => void;
  setNewEntry: (val: Nullable<EditEntry>) => void;
  newEntry: Nullable<EditEntry>;
  newAbsence: boolean;
  setNewAbsence: (val: boolean) => void;
  filterTab: number;
  newEntryTalentData?: NewEntryTalentData;
};

const EntriesTable = ({
  groupedData,
  supplementHeaders,
  expensesHeaders,
  selectedEntries,
  setSelectedEntries,
  filterTab,
  newEntry,
  setNewEntry,
  newAbsence,
  setNewAbsence,
  newEntryTalentData,
}: Props) => {
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [collabsibleStates, setColabsibleStates] = useState<CollabsibleStates>({
    supplementsOpen: false,
    expensesOpen: false,
  });

  const supplementsHeaderRefs = useRef([]);
  const expensesHeaderRefs = useRef([]);

  const [selectedEntry, setSelectedEntry] = useState<Entry | null>(null);

  const entries = useAppSelector((state) => selectAllEntries(state));
  const [copyModalOpen, setCopyModalOpen] = useState<boolean>(false);

  const handleSelectAllEntries = (checked: boolean) => {
    if (!checked) {
      setSelectedEntries([]);
      return setSelectedGroups([]);
    }

    let groupLabels = [];

    for (const key in groupedData) {
      const object = groupedData[key];
      const keys = Object.keys(object);
      groupLabels.push(key);
      groupLabels.push(...keys);
    }

    setSelectedEntries(entries);
    setSelectedGroups(groupLabels);
  };

  const handleCheckGroupEntries = (groupData: Entry[], groupLabel: string) => {
    setSelectedGroups([...selectedGroups, groupLabel]);
    setSelectedEntries([...selectedEntries, ...groupData]);
  };

  const handleCheckNestedGroupEntries = (
    entries: Entry[],
    groups: string[],
    groupLabel: string,
  ) => {
    setSelectedEntries([...selectedEntries, ...entries]);
    setSelectedGroups([...groups, groupLabel]);
  };

  const handleUnCheckNestedGroupEntries = (
    entries: Entry[],
    groups: string[],
    groupLabel: string,
  ) => {
    setSelectedEntries(
      selectedEntries.filter(
        (selectedEntry) =>
          !entries.some((entry) => entry.id === selectedEntry.id),
      ),
    );
    return setSelectedGroups(
      selectedGroups.filter(
        (group) => !groups.some((g) => g === group) && group !== groupLabel,
      ),
    );
  };

  const handleUnCheckGroupEntries = (
    groupData: Entry[],
    groupLabel: string,
  ) => {
    setSelectedEntries(
      selectedEntries.filter(
        (selectedEntry) =>
          !groupData.some((entry) => entry.id === selectedEntry.id),
      ),
    );
    return setSelectedGroups(
      selectedGroups.filter((group) => group !== groupLabel),
    );
  };

  const handleSelectGroupEntries = (
    groupData: Entry[] | object,
    groupLabel: string,
    checked: boolean,
  ) => {
    if (Array.isArray(groupData)) {
      if (checked) {
        return handleUnCheckGroupEntries(groupData, groupLabel);
      }
      return handleCheckGroupEntries(groupData, groupLabel);
    }
    const entries = Object.values(groupData).flat();
    const groups = Object.keys(groupData);

    if (checked) {
      return handleUnCheckNestedGroupEntries(entries, groups, groupLabel);
    }
    return handleCheckNestedGroupEntries(entries, groups, groupLabel);
  };

  const handleSelectSingleEntriesCheckbox = (
    event: React.MouseEvent<HTMLTableCellElement, MouseEvent>,
    selectedEntry: Entry,
  ) => {
    // Stops checkbox click from affecting whole row (which has it's own onclick event)
    event.stopPropagation();

    const isSelected = selectedEntries.find(
      (entry) => entry.id === selectedEntry.id,
    );

    isSelected
      ? setSelectedEntries(
          selectedEntries.filter((entry) => entry.id !== selectedEntry.id),
        )
      : setSelectedEntries([...selectedEntries, selectedEntry]);
  };

  const handleSingleEntryRowClick = (selectedEntry: Entry) => {
    setSelectedEntry(selectedEntry);
  };

  const openEntryForCopy = (entry: Entry) => {
    setSelectedEntries([entry]);
    setCopyModalOpen(true);
  };

  const closeCopyModal = () => {
    setSelectedEntries([]);
    setSelectedEntry(null);
    setCopyModalOpen(false);
  };

  const renderTable = (data: any) => {
    if (Array.isArray(data)) {
      // Generate table rows from array of items
      return data.map((item: Entry, index: number) => (
        <GroupEntryRow
          key={item.id + "_" + index}
          item={item}
          index={index}
          handleSelectSingleEntriesCheckbox={handleSelectSingleEntriesCheckbox}
          handleSingleEntryRowClick={handleSingleEntryRowClick}
          selectedEntries={selectedEntries}
          collabsibleStates={collabsibleStates}
          headers={{
            supplementHeaders,
            supplementsHeaderRefs,
            expensesHeaders,
            expensesHeaderRefs,
          }}
          filterTab={filterTab}
        />
      ));
    }

    // Generate table rows from object of subgroups
    const groupLabels = Object.keys(data);
    const firstGroupingLevelLabels = Object.keys(groupedData);

    return groupLabels.map((groupLabel, index) => {
      return (
        <GroupRow
          key={groupLabel + "_" + index}
          firstGroupingLevelLabels={firstGroupingLevelLabels}
          groupData={data[groupLabel]}
          groupLabel={groupLabel}
          setColabsibleStates={setColabsibleStates}
          selectedEntries={selectedEntries}
          collabsibleStates={collabsibleStates}
          renderTable={renderTable}
          handleSelectGroupEntries={handleSelectGroupEntries}
          headers={{
            supplementHeaders,
            supplementsHeaderRefs,
            expensesHeaders,
            expensesHeaderRefs,
          }}
        />
      );
    });
  };

  const checked = !!entries.length && entries.length === selectedEntries.length;

  return (
    <>
      <TableContainer
        sx={{
          maxHeight: "64vh",
          overflow: "auto",
          position: "relative",
          width: "100%",
        }}
        component={Paper}
      >
        <Table
          sx={{
            "& .row_even": {
              backgroundColor: filterTab === 1 ? "#FFF3E0" : "#FAFAFB",
              verticalAlign: "top",
            },
            "& .row_odd": {
              backgroundColor:
                filterTab === 1 ? "rgba(255, 250, 242, 1)" : "#FFF",
              verticalAlign: "top",
            },
          }}
        >
          <EntriesHeaders
            collabsibleStates={collabsibleStates}
            handleSelectAllEntries={handleSelectAllEntries}
            checked={checked}
          />
          <TableBody>{groupedData && renderTable(groupedData)}</TableBody>
        </Table>
        <BottomActionBar
          selectedEntries={selectedEntries}
          setSelectedEntries={setSelectedEntries}
          copyEntry={() => setCopyModalOpen(true)}
        />
      </TableContainer>

      <EntriesModals
        selectedEntry={selectedEntry}
        setSelectedEntry={setSelectedEntry}
        newEntry={newEntry}
        setNewEntry={setNewEntry}
        openCopyModal={openEntryForCopy}
        newAbsence={newAbsence}
        setNewAbsence={setNewAbsence}
        newEntryTalentData={newEntryTalentData}
      />
      {copyModalOpen && (
        <CopyEntries
          isOpen={copyModalOpen}
          closeModal={closeCopyModal}
          entries={selectedEntries}
        />
      )}
    </>
  );
};

export default EntriesTable;
